import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getLocalizedExperienceYears } from '../utils/experienceYears';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ExperienceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ExperienceItem = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const JobTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const Company = styled.div`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Period = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 1rem;
`;

const Description = styled.p`
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const TechTag = styled.span`
  background-color: rgba(252, 232, 232, 0.1);
  color: ${({ theme }) => theme.colors.text};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const ExperiencePage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      import("../utils/secureStorage").then(({ getSecureStorage }) => {
        const secureData = getSecureStorage();
        const token = secureData.token;
        if (token) {
          setIsLoggedIn(true);
        }
      });
    } catch (e) {}
  }, []);

  const { t, i18n } = useTranslation();
  const experienceYears = getLocalizedExperienceYears(i18n.language, 2019);
  
  const experiences = t('experience.items', {
    returnObjects: true,
    experienceYears,
  }) as any[];
  const experiencesWithoutNoLogin = experiences.map((item: any) => {
    return {
      ...item,
      title: "XXXX XXXX XXXX",
      company: "XXXX XXXX XXXX",
      period: "XXXX XXXX XXXX",
      description: "XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX",
    };
  });

  return (
    <PageContainer as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
      <PageTitle variants={itemVariants}>{t('experience.title')}</PageTitle>
      
      <ExperienceList>
        { isLoggedIn ? experiences.map((job: any, index: number) => (
          <ExperienceItem key={index} variants={itemVariants}>
            <JobTitle>{job.title}</JobTitle>
            <Company>{job.company}</Company>
            <Period>{job.period}</Period>
            <Description>{job.description}</Description>
            <TechStack>
              {job.techStack.map((tech: string, techIndex: number) => (
                <TechTag key={techIndex}>{tech}</TechTag>
              ))}
            </TechStack>
          </ExperienceItem>
        )) : experiencesWithoutNoLogin.map((item: any, index: number) => (
          <ExperienceItem key={index} variants={itemVariants} className="blur-effect" onClick={() => navigate("/login")}>
            <JobTitle>{item.title}</JobTitle>
            <Company>{item.company}</Company>
            <Period>{item.period}</Period>
            <Description>{item.description}</Description>
          </ExperienceItem>
          ))}
      </ExperienceList>
    </PageContainer>
  );
};

export default ExperiencePage; 